import {
  AppNamespaceEnum,
  AiConversationInput,
} from "@/services/fobizzToolsApi";

const PAGINATION_LIMIT = 16;

export function useConversationQueries() {
  const queryClient = useQueryClient();
  const { aiConversations } = useApiClient();

  const isConversationsLoading = useIsFetching({ queryKey: ["conversations"] });

  const indexQuery = (query: Ref<string>, assistantCategory?: string) => {
    return useInfiniteQuery({
      queryKey: ["conversations", query, assistantCategory],
      queryFn: ({ pageParam }) => {
        if (query.value.length > 0) {
          return aiConversations.searchAiConversations({
            page: pageParam,
            limit: PAGINATION_LIMIT,
            query: query.value,
          });
        } else {
          return aiConversations.listAiConversations({
            page: pageParam,
            limit: PAGINATION_LIMIT,
            query: query.value,
            ...(assistantCategory && { assistantCategory: assistantCategory }),
          });
        }
      },
      initialPageParam: 0,
      getPreviousPageParam: (lastPage, _pages) => lastPage.prevPage,
      getNextPageParam: (lastPage, _pages) => lastPage.nextPage,
    });
  };

  const getQuery = (conversationId: Ref<string>) => {
    return useQuery(
      {
        queryKey: ["conversation", conversationId],
        queryFn: () =>
          aiConversations.getAiConversation({
            id: conversationId.value,
          }),
      },
      queryClient
    );
  };

  const useDeleteMutation = (id: string) => {
    return useMutation(
      {
        mutationFn: () => aiConversations.deleteAiConversation({ id: id }),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["conversations"],
          });
        },
      },
      queryClient
    );
  };

  const useCreateMutation = () => {
    return useMutation(
      {
        mutationFn: (params: Partial<AiConversationInput>) =>
          aiConversations.createAiConversation({
            requestBody: { conversation: params },
          }),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["conversations"],
          });
          queryClient.invalidateQueries({
            queryKey: [AppNamespaceEnum.AI_ASSISTANT, "recentlyUsed"],
          });
        },
      },
      queryClient
    );
  };

  const useUpdateMutation = (conversationId: string) => {
    return useMutation(
      {
        mutationFn: (params: Partial<AiConversationInput>) =>
          aiConversations.updateAiConversation({
            id: conversationId,
            requestBody: { conversation: params },
          }),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["conversations"],
          });
          queryClient.invalidateQueries({
            queryKey: ["conversation", conversationId],
          });
        },
      },
      queryClient
    );
  };

  return {
    indexQuery,
    getQuery,
    useCreateMutation,
    useUpdateMutation,
    useDeleteMutation,
    isConversationsLoading,
  };
}
